/** @prettier */

/**
 * グループ設定用ツリービュー
 */

import {useEffect, useState} from 'react';
import {TreeView, TreeItem} from '@mui/x-tree-view';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import {useNavigate, useLocation, useParams} from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import {Box} from '@mui/material';

//API import
import {getCompositionsSitesBySiteID} from '../API/API_Collection';
//interface import
import * as apiIF from '../Type/api';
//Style import
import '../css/common/common.css';

import {iconBlack} from '../define';

import {useTranslation} from 'react-i18next';

type FloorInfoType = {
  floor: string;
  floorName: string;
}[];

/**
 * グループ設定画面　ツリービュー
 * - defaultExpanded string配列で指定。デフォルトで開く階層を指定する。開きたい階層に対して、ツリーのIDに指定した文字列を、カンマ繋ぎで配列に指定する。
 * - groupInfoData グループ情報
 * - unitSystem グループに登録されている空調機の通信設定
 * @param props props
 * @returns JSX
 */
const GroupSettingTreeView = (props: {
  defaultExpanded: string[];
  groupInfoData: apiIF.res.getCompositionsGroups | null;
  unitSystem: 'sl' | 'rt' | 'modbus';
}) => {
  const navigate = useNavigate();
  //URL取得
  const location = useLocation();
  //パスパラメータ取得
  const {siteID} = useParams();

  const [floorNameList, setFloorNameList] = useState<FloorInfoType>([]); //フロアの名前一覧
  const [expanded, setExpanded] = useState<string[]>(props.defaultExpanded); //ツリービュー展開する位置

  const trans = useTranslation().t; //翻訳関数

  //ツリービュー展開する位置を更新
  useEffect(() => {
    setExpanded(props.defaultExpanded);
  }, [props.defaultExpanded]);

  // サイト情報の取得・フロア情報の設定
  useEffect(() => {
    //パスパラメータを取得できない場合
    if (siteID === undefined) {
      return;
    }

    getCompositionsSitesBySiteID(siteID).then(response => {
      //フロアの名前を格納する配列をサイトに存在しているフロア分作成
      //フロアの順番を降順にする
      const temp: FloorInfoType = [
        ...Array(
          Math.abs(Number(response['under-ground'])) +
            Number(response['above-ground']) +
            1
        ),
      ]
        .map((_, i) => {
          const currentFloor: number = i + Number(response['under-ground']);
          return {
            floor: String(currentFloor),
            floorName:
              currentFloor < 0
                ? `Basement${Math.abs(currentFloor)}`
                : `Floor${currentFloor}`,
          };
        })
        .reverse();
      setFloorNameList(temp);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * ツリービューが展開・折りたたまれた時の処理
   * @param event
   * @param nodeIds
   */
  const handleToggle = (event: React.SyntheticEvent, nodeIds: string[]) => {
    setExpanded(nodeIds);
  };

  //prettier-ignore
  type GroupTreeItemType = {
    id          : string;
    nodeID      : string;
    className   ?: string;
    label       : React.ReactNode;
    labelClick  ?: React.MouseEventHandler<HTMLButtonElement>;
    children    ?: GroupTreeItemType[];
  };
  /**
   * SL通信設定用のツリービューの項目
   * @param groupInfo グループの情報
   * @param floorNameList フロアの名前一覧
   * @returns ツリービューの項目
   */
  const slGroupTreeItemData = (
    groupInfo: apiIF.resParameters.groupInformations[],
    floorNameList: FloorInfoType
  ) => {
    const treeItem: GroupTreeItemType[] = floorNameList.map(floorInfo => {
      let init: GroupTreeItemType[] = [];
      //prettier-ignore
      return {
        //フロア
        id          : `treeLink${floorInfo.floor}`,
        nodeID      : floorInfo.floor,
        label       : trans(
          `GroupSettingTreeView.label.floor.${floorInfo.floorName.toLowerCase()}`
        ), //floorNameList内で翻訳すると画面リロード時に翻訳されないため、ここで翻訳する
        labelClick  : () => {
          navigate(
            `/pages/my-site/site-detail/${siteID}/floor-detail/${floorInfo.floor}/group-registration${location.search}`
          );
        },
        children    : groupInfo.reduce((result, groupInfo) => {
          if (groupInfo['floor'] !== floorInfo.floor) return result;
          result.push({
            //グループ
            id          : `treeLink${groupInfo['group-id']}`,
            nodeID      : groupInfo['group-id'],
            label       : groupInfo['group-name'],
            labelClick  : () => {
              navigate(
                `/pages/my-site/site-detail/${siteID}/floor-detail/${floorInfo.floor}/group-registration/${groupInfo['group-id']}/group-layout-registration${location.search}`
              );
            },
            //室内機
            children    : groupInfo['unit-informations'] === undefined ? [] : groupInfo['unit-informations'].map(iuInfo => ({
              id      : `treeLink${iuInfo['unit-id']}`,
              nodeID  : iuInfo['unit-id'],
              label   : iuInfo['unit-name'],
            })),
          });
          return result;
        }, init),
      };
    });

    return treeItem;
  };
  /**
   * Hyozan通信設定用のツリービューの項目
   * @param groupInfo グループの情報
   * @returns ツリービューの項目
   */
  const hyozanGroupTreeItemData = (
    groupInfo: apiIF.resParameters.groupInformations[]
  ) => {
    //prettier-ignore
    const treeItem: GroupTreeItemType = {
      id          : `treeLinkFloor0`,
      nodeID      : 'Floor0',
      label       : trans('GroupSettingTreeView.item.site'),
      labelClick  : () => {
        navigate(
          `/pages/my-site/site-detail/${siteID}/hyozan-group-registration${location.search}`
        );
      },
      children    : groupInfo.map(groupInfo => ({
        id          : `treeLink${groupInfo['group-id']}`,
        nodeID      : groupInfo['group-id'],
        label       : groupInfo['group-name'],
        labelClick  : () => {
          navigate(
            `/pages/my-site/site-detail/${siteID}/hyozan-group-registration/${groupInfo['group-id']}/hyozan-group-layout-registration${location.search}`
          );
        },
        //室内機
        children    : groupInfo['unit-informations'] === undefined ? [] : groupInfo['unit-informations'].map(iuInfo => ({
          id      : `treeLink${iuInfo['unit-id']}`,
          nodeID  : iuInfo['unit-id'],
          label   : iuInfo['unit-name'],
        })),
      })),
    };

    return treeItem;
  };
  /**
   * Modbus通信設定用のツリービューの項目
   * @param groupInfo グループの情報
   * @returns ツリービューの項目
   */
  const modbusGroupTreeItemData = (
    groupInfo: apiIF.resParameters.groupInformations[]
  ) => {
    //prettier-ignore
    const treeItem: GroupTreeItemType = {
      id          : `treeLinkFloor0`,
      nodeID      : 'Floor0',
      label       : trans('GroupSettingTreeView.item.site'),
      labelClick  : () => {
        navigate(
          `/pages/my-site/site-detail/${siteID}/modbus-group-registration${location.search}`
        );
      },
      children    : groupInfo.map(groupInfo => ({
        id          : `treeLink${groupInfo['group-id']}`,
        nodeID      : groupInfo['group-id'],
        label       : groupInfo['group-name'],
        labelClick  : () => {
          navigate(
            `/pages/my-site/site-detail/${siteID}/modbus-group-registration/${groupInfo['group-id']}/modbus-group-layout-registration${location.search}`
          );
        },
        //室内機
        children    : groupInfo['unit-informations'] === undefined ? [] : groupInfo['unit-informations'].map(iuInfo => ({
          id      : `treeLink${iuInfo['unit-id']}`,
          nodeID  : iuInfo['unit-id'],
          label   : iuInfo['unit-name'],
        })),
      })),
    };

    return treeItem;
  };
  /**
   * ツリービュー作成
   * @param data ツリービューの項目
   * @returns JSX
   */
  const RenderTree = (data: GroupTreeItemType) => (
    <TreeItem
      key={data.id}
      nodeId={data.nodeID}
      id={data.id}
      label={
        <>
          <Box
            component="span"
            className={`groupSetting__treeView__item ${
              data.className === undefined ? '' : data.className
            }`}
            title={data.label?.toString()}>
            {data.label}
          </Box>
          {/** 末端ではない場合、遷移ボタンを作成 */}
          {data.labelClick === undefined ? (
            <></>
          ) : (
            <IconButton onClick={data.labelClick}>
              <img
                src={iconBlack.CHEVRON_RIGHT.src}
                alt={`${trans(
                  `GroupSettingTreeView.image.alternativeText.${iconBlack.CHEVRON_RIGHT.alt}`
                )}`}
              />
            </IconButton>
          )}
        </>
      }>
      {Array.isArray(data.children)
        ? data.children.map(data => RenderTree(data))
        : null}
    </TreeItem>
  );

  if (props.groupInfoData === null) return <></>;
  return (
    <TreeView
      disableSelection={true}
      defaultCollapseIcon={<RemoveIcon />}
      defaultExpandIcon={<AddIcon />}
      expanded={expanded}
      onNodeToggle={handleToggle}
      className="groupSetting__treeView">
      {props.unitSystem === 'sl' && floorNameList.length > 0 ? (
        //SL
        slGroupTreeItemData(
          props.groupInfoData['group-informations'].filter(
            groupInfo => groupInfo['unit-system'] === 'SL'
          ),
          floorNameList
        ).map(treeItem => RenderTree(treeItem))
      ) : props.unitSystem === 'rt' ? (
        //Hyozan
        RenderTree(
          hyozanGroupTreeItemData(
            props.groupInfoData['group-informations'].filter(
              groupInfo => groupInfo['unit-system'] === 'RT'
            )
          )
        )
      ) : props.unitSystem === 'modbus' ? (
        //Modbus
        RenderTree(
          modbusGroupTreeItemData(
            props.groupInfoData['group-informations'].filter(
              groupInfo => groupInfo['unit-system'] === 'Modbus'
            )
          )
        )
      ) : (
        <></>
      )}
    </TreeView>
  );
};
export default GroupSettingTreeView;
